import React, { useState } from 'react';
import { Users, ArrowRight, Filter, ShieldOff, PieChart, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, Button, Input, ProgressBar } from '../components/ui/Primitives';
export function Reassignment() {
  const [selectedMode, setSelectedMode] = useState('Round Robin');
  const [activeDepts, setActiveDepts] = useState<string[]>(['East Sales']);
  const [showSimulation, setShowSimulation] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const toggleDept = (dept: string) => {
    setActiveDepts(prev => prev.includes(dept) ? prev.filter(d => d !== dept) : [...prev, dept]);
  };
  const handleRun = () => {
    setShowConfirmModal(false);
    setIsRunning(true);
    setProgress(0);
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          return 100;
        }
        return p + 10;
      });
    }, 300);
  };
  return <div className="h-full flex flex-col max-w-7xl mx-auto relative" data-id="element-670">
      {showConfirmModal && <div className="absolute inset-0 z-50 flex items-center justify-center" data-id="element-671">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => setShowConfirmModal(false)} data-id="element-672" />
          <Card className="w-[400px] p-6 relative z-10 shadow-2xl animate-in zoom-in-95" data-id="element-673">
            <div className="flex items-center gap-3 text-amber-600 mb-4" data-id="element-674">
              <AlertTriangle className="w-6 h-6" data-id="element-675" />
              <h3 className="text-lg font-semibold text-slate-900" data-id="element-676">
                Confirm Reassignment
              </h3>
            </div>
            <p className="text-sm text-slate-600 mb-6" data-id="element-677">
              You are about to reassign <strong data-id="element-678">1,847</strong> prospects via{' '}
              <strong data-id="element-679">{selectedMode}</strong>. This action cannot be easily
              undone.
            </p>
            <div className="flex justify-end gap-3" data-id="element-680">
              <Button variant="outline" onClick={() => setShowConfirmModal(false)} data-id="element-681">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleRun} data-id="element-682">
                Confirm & Run
              </Button>
            </div>
          </Card>
        </div>}

      <div className="mb-6" data-id="element-683">
        <h1 className="text-xl font-semibold text-slate-900" data-id="element-684">
          Bulk Reassignment
        </h1>
        <p className="text-sm text-slate-500" data-id="element-685">
          Redistribute prospects across users or teams.
        </p>
      </div>

      <div className="flex gap-6 flex-1 min-h-0" data-id="element-686">
        {/* Left: Source */}
        <Card className="w-1/3 p-5 flex flex-col" data-id="element-687">
          <h2 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-688">
            1. Source Selection
          </h2>

          <div className="space-y-4 flex-1" data-id="element-689">
            <div data-id="element-690">
              <label className="text-xs font-medium text-slate-700 mb-1.5 block" data-id="element-691">
                Current Owner
              </label>
              <Input placeholder="Search users..." data-id="element-692" />
            </div>

            <div data-id="element-693">
              <label className="text-xs font-medium text-slate-700 mb-1.5 block" data-id="element-694">
                Department Filter
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-695">
                {['East Sales', 'Refi Team', 'Inbound'].map(d => <span key={d} onClick={() => toggleDept(d)} className={`px-2.5 py-1 rounded-md text-xs cursor-pointer transition-colors ${activeDepts.includes(d) ? 'bg-electric-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-696">
                    {d}
                  </span>)}
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200" data-id="element-697">
              <label className="text-xs font-medium text-slate-700 mb-3 block" data-id="element-698">
                Exclusions
              </label>
              <div className="space-y-2" data-id="element-699">
                <label className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 bg-slate-50" data-id="element-700">
                  <input type="checkbox" className="rounded border-slate-300 text-electric-500" defaultChecked data-id="element-701" />
                  <ShieldOff className="w-4 h-4 text-slate-400" data-id="element-702" />
                  <span className="text-sm text-slate-700" data-id="element-703">
                    Exclude DNC records
                  </span>
                </label>
                <label className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 bg-slate-50" data-id="element-704">
                  <input type="checkbox" className="rounded border-slate-300 text-electric-500" defaultChecked data-id="element-705" />
                  <span className="text-sm text-slate-700" data-id="element-706">
                    {'Exclude inactive > 30 days'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="mt-4 p-4 bg-electric-50 rounded-lg border border-electric-100 text-center" data-id="element-707">
            <div className="text-2xl font-bold text-electric-700 font-mono" data-id="element-708">
              1,847
            </div>
            <div className="text-xs text-electric-600 font-medium uppercase tracking-wider mt-1" data-id="element-709">
              Selected Prospects
            </div>
          </div>
        </Card>

        {/* Center: Mode */}
        <div className="w-1/3 flex flex-col items-center py-12" data-id="element-710">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-8" data-id="element-711">
            <ArrowRight className="w-6 h-6 text-slate-400" data-id="element-712" />
          </div>

          <div className="w-full space-y-3" data-id="element-713">
            <h2 className="text-sm font-semibold text-slate-800 mb-4 text-center" data-id="element-714">
              2. Reassignment Mode
            </h2>
            <ModeCard title="Single User" desc="Transfer all to one person" selected={selectedMode === 'Single User'} onClick={() => setSelectedMode('Single User')} data-id="element-715" />
            <ModeCard title="Round Robin" desc="Distribute evenly across a team" selected={selectedMode === 'Round Robin'} onClick={() => setSelectedMode('Round Robin')} data-id="element-716" />
            <ModeCard title="Weighted" desc="Distribute based on capacity" selected={selectedMode === 'Weighted'} onClick={() => setSelectedMode('Weighted')} data-id="element-717" />
          </div>
        </div>

        {/* Right: Target */}
        <Card className="w-1/3 p-5 flex flex-col" data-id="element-718">
          <h2 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-719">
            3. Target Selection
          </h2>

          <div className="space-y-4 flex-1" data-id="element-720">
            <div data-id="element-721">
              <label className="text-xs font-medium text-slate-700 mb-1.5 block" data-id="element-722">
                Select Team/Users
              </label>
              <Input placeholder="Search to add users..." data-id="element-723" />
            </div>

            <div className="space-y-2" data-id="element-724">
              <UserRow name="Sarah Jenkins" weight="1.0" data-id="element-725" />
              <UserRow name="Mike Chen" weight="1.0" data-id="element-726" />
              <UserRow name="David Smith" weight="0.5" data-id="element-727" />
              <UserRow name="Emily Davis" weight="1.0" data-id="element-728" />
            </div>
          </div>

          <Button variant="outline" className="w-full mt-4" onClick={() => setShowSimulation(true)} data-id="element-729">
            <PieChart className="w-4 h-4 mr-2" data-id="element-730" /> Simulate Distribution
          </Button>

          {showSimulation && <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg animate-in slide-in-from-top-2" data-id="element-731">
              <h4 className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider" data-id="element-732">
                Simulated Results
              </h4>
              <div className="space-y-2 text-sm text-slate-600" data-id="element-733">
                <div className="flex justify-between" data-id="element-734">
                  <span data-id="element-735">Sarah Jenkins</span>
                  <span className="font-mono font-medium" data-id="element-736">527</span>
                </div>
                <div className="flex justify-between" data-id="element-737">
                  <span data-id="element-738">Mike Chen</span>
                  <span className="font-mono font-medium" data-id="element-739">527</span>
                </div>
                <div className="flex justify-between" data-id="element-740">
                  <span data-id="element-741">David Smith</span>
                  <span className="font-mono font-medium" data-id="element-742">266</span>
                </div>
                <div className="flex justify-between" data-id="element-743">
                  <span data-id="element-744">Emily Davis</span>
                  <span className="font-mono font-medium" data-id="element-745">527</span>
                </div>
              </div>
            </div>}
        </Card>
      </div>

      {/* Bottom Action */}
      <div className="mt-6 p-4 bg-surface border border-slate-200 rounded-xl shadow-sm flex items-center justify-between sticky bottom-0" data-id="element-746">
        {isRunning ? <div className="w-full flex items-center gap-6" data-id="element-747">
            <div className="flex-1" data-id="element-748">
              <div className="flex justify-between text-sm mb-1" data-id="element-749">
                <span className="font-medium text-slate-700" data-id="element-750">
                  {progress === 100 ? 'Reassignment Complete' : 'Reassigning Prospects...'}
                </span>
                <span className="font-mono text-electric-600" data-id="element-751">{progress}%</span>
              </div>
              <ProgressBar progress={progress} active={progress < 100} data-id="element-752" />
            </div>
            {progress === 100 && <Button variant="outline" onClick={() => setIsRunning(false)} data-id="element-753">
                Done
              </Button>}
          </div> : <>
            <div data-id="element-754">
              <div className="text-sm font-semibold text-slate-900" data-id="element-755">
                Ready to Reassign
              </div>
              <div className="text-xs text-slate-500" data-id="element-756">
                Move 1,847 prospects from John D. → 4 reps via {selectedMode}
              </div>
            </div>
            <Button variant="primary" size="lg" onClick={() => setShowConfirmModal(true)} data-id="element-757">
              Run Reassignment
            </Button>
          </>}
      </div>
    </div>;
}
function ModeCard({
  title,
  desc,
  selected,
  onClick
}: any) {
  return <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${selected ? 'border-electric-500 bg-electric-50/50 shadow-sm' : 'border-slate-200 hover:border-slate-300 bg-white'}`} onClick={onClick} data-id="element-758">
      <div className="font-semibold text-slate-900 text-sm" data-id="element-759">{title}</div>
      <div className="text-xs text-slate-500 mt-1" data-id="element-760">{desc}</div>
    </div>;
}
function UserRow({
  name,
  weight
}: any) {
  return <div className="flex items-center justify-between p-2 rounded-lg border border-slate-200 bg-white" data-id="element-761">
      <div className="flex items-center gap-2" data-id="element-762">
        <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-medium text-slate-600" data-id="element-763">
          {name.split(' ').map((n: string) => n[0]).join('')}
        </div>
        <span className="text-sm font-medium text-slate-700" data-id="element-764">{name}</span>
      </div>
      <div className="flex items-center gap-2" data-id="element-765">
        <span className="text-[10px] text-slate-400 uppercase" data-id="element-766">Weight</span>
        <input type="text" defaultValue={weight} className="w-12 h-6 text-xs text-center border border-slate-200 rounded bg-slate-50" data-id="element-767" />
      </div>
    </div>;
}